// LotWise Home — what LotWise performs directly vs. coordinates vs. does NOT do.
// This governs the "What LotWise Handles" section and every place we describe scope.

export const performedServices = [
  { title: "Property photography", body: "Interior, exterior, room, and feature capture built for MLS-ready and web-ready use." },
  { title: "Video walkthroughs", body: "Guided property video by quote — designed to support a stronger online presentation." },
  { title: "Aerial / drone capture", body: "Exterior drone imagery where appropriate, subject to location, weather, flight conditions, and applicable rules." },
  { title: "3D / AI walkthroughs", body: "AI walkthrough tours by quote for listings where a stronger guided online experience supports the property." },
  { title: "Property information & feature capture", body: "Room notes, upgrades, and highlight details organized for the listing." },
  { title: "Visual presentation planning", body: "Framing, sequence, and composition planning so every image supports the listing narrative." },
  { title: "Listing preparation", body: "MLS-ready and web-ready photo exports, delivery folders, and a listing-readiness checklist per property." },
  { title: "Marketing asset selection", body: "Social-ready selections, feature sheet support materials, and print-friendly picks where included." },
  { title: "Ongoing listing update support", body: "Recurring capture and asset refresh across monthly and team plans." },
  { title: "End-to-end project coordination", body: "One point of contact for the pieces that need to move together before the property goes live." },
];

export const coordinatedServices = [
  { title: "Floor plan documentation", body: "Coordinated through approved providers on request." },
  { title: "Staging referrals", body: "Introductions to stagers where a property would benefit — LotWise Home does not stage physically." },
  { title: "Print production", body: "Feature sheets and printed collateral coordinated via approved partners." },
  { title: "Website listing upload support", body: "Handoff and upload assistance for agent and brokerage sites where approved." },
  { title: "Additional media specialists", body: "Twilight, virtual tour, and specialty capture coordinated where scope requires it." },
];

// Explicit "not performed" list — used to eliminate construction / renovation confusion.
export const notPerformed = [
  "Renovations, remodelling, or construction work",
  "Physical property staging (referrals only)",
  "MLS uploads or publishing (owned by the licensed agent or brokerage)",
  "Sales, negotiation, or offer handling",
  "Guarantees of sale price, showings, offers, or MLS placement",
];

// Audience paths — primary ICP first, secondary paths follow.
export const audiencePaths = [
  {
    slug: "agents",
    label: "For Agents & Teams",
    intro: "Handled listing coverage across every property you bring to market — photography, presentation, and listing readiness on a repeatable rhythm.",
    ctaLabel: "See the agent path",
  },
  {
    slug: "brokerage",
    label: "For Brokerages",
    intro: "A consistent listing standard across every office and every agent, with priority scheduling and workflow support.",
    ctaLabel: "See the brokerage path",
  },
  {
    slug: "builders",
    label: "For Builders & Developers",
    intro: "Media and presentation for new developments, spec builds, and pre-market sites — coordinated as one project.",
    ctaLabel: "See the builder path",
  },
  {
    slug: "owners",
    label: "For Investors & Property Owners",
    intro: "Presentation support for investment properties, rentals, and owner-marketed sales, coordinated end-to-end.",
    ctaLabel: "See the owner path",
  },
];

// Intake selector options
export const propertyTypes = [
  { value: "residential",    label: "Residential listing" },
  { value: "development",    label: "New development" },
  { value: "investment",     label: "Investment property" },
  { value: "rental",         label: "Rental" },
  { value: "commercial",     label: "Commercial property" },
  { value: "other",          label: "Other" },
];

export const helpTypes = [
  { value: "media",       label: "Photography / media" },
  { value: "prep",        label: "Listing preparation" },
  { value: "marketing",   label: "Marketing" },
  { value: "coordination",label: "Coordination" },
  { value: "full",        label: "Full end-to-end support" },
  { value: "not-sure",    label: "Not sure yet" },
];

// Simple recommendation matrix. Returns { plan, services, next }.
export function recommend(propertyType, helpType) {
  const base = {
    plan: "listing-standard",
    planLabel: "Listing Standard",
    services: ["Property photography", "Room and feature capture", "MLS-ready and web-ready exports"],
    next: "Request a listing plan",
  };

  if (helpType === "full")            { base.plan = "media-walkthrough"; base.planLabel = "Media Walkthrough Package"; }
  if (helpType === "coordination")    { base.plan = "listing-plus";      base.planLabel = "Listing Plus"; }
  if (propertyType === "development") { base.plan = "brokerage-agency";  base.planLabel = "Custom project plan"; }
  if (propertyType === "commercial")  { base.plan = "brokerage-agency";  base.planLabel = "Custom project plan"; }
  if (propertyType === "rental" && helpType === "media") { base.plan = "listing-essential"; base.planLabel = "Listing Essential"; }

  if (helpType === "full" || propertyType === "development") {
    base.services = [
      "Property photography",
      "Video walkthrough",
      "AI walkthrough (by quote)",
      "Drone exterior capture where available",
      "Listing preparation",
      "Marketing asset selection",
      "End-to-end project coordination",
    ];
  } else if (helpType === "media") {
    base.services = ["Property photography", "Video walkthrough (optional)", "Drone (where available)", "MLS-ready + web-ready exports"];
  } else if (helpType === "prep") {
    base.services = ["Property photography", "Room and feature capture", "Property highlight notes", "Listing-readiness checklist"];
  } else if (helpType === "marketing") {
    base.services = ["Social-ready image selections", "Feature sheet support materials", "Website listing support"];
  } else if (helpType === "coordination") {
    base.services = ["Listing project coordination", "Vendor coordination", "Ongoing listing update support"];
  }

  return base;
}
